import { useState } from "react";
import { Copy, Check, Mail } from "lucide-react";
import { toast } from "sonner";

/**
 * Email address with a small "copy" affordance.
 * - `tone="dark"` for use on the navy-deep / cream-on-dark sections
 * - `tone="light"` (default) for cream backgrounds
 * - `showIcon` toggles the leading mail icon
 */
export default function EmailCopy({
  email,
  tone = "light",
  showIcon = true,
  className = "",
  testid = "email-copy",
}) {
  const [copied, setCopied] = useState(false);

  const onCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      toast.success("Email copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = email;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        toast.success("Email copied");
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.error("Couldn't copy — please copy manually.");
      }
      ta.remove();
    }
  };

  const dark = tone === "dark";
  const linkCls = dark
    ? "text-cream hover:text-teal-mist"
    : "text-ink hover:text-navy";
  const btnCls = dark
    ? "text-cream/70 hover:text-cream hover:bg-cream/10 border border-cream/15"
    : "text-ink-soft hover:text-navy hover:bg-navy/5 border border-sand/70";

  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-2 ${className}`}
    >
      {showIcon && (
        <Mail
          size={16}
          className={dark ? "text-teal-mist" : "text-navy"}
        />
      )}
      <a
        href={`mailto:${email}`}
        data-testid={`${testid}-mailto`}
        className={`${linkCls} transition-colors break-all`}
      >
        {email}
      </a>
      <button
        type="button"
        onClick={onCopy}
        data-testid={`${testid}-button`}
        aria-label={copied ? "Email copied" : "Copy email to clipboard"}
        title={copied ? "Copied" : "Copy email"}
        className={`inline-flex items-center justify-center h-7 w-7 rounded-full ${btnCls} transition-all active:scale-95 flex-shrink-0`}
      >
        {copied ? <Check size={13} /> : <Copy size={13} />}
      </button>
    </span>
  );
}
